/**
 * Refresh-once-on-401 for the browser Supabase client.
 *
 * The access token is judged on the server clock and refreshed ahead of
 * expiry (src/lib/auth/accessToken.ts), but a phone that slept past the
 * expiry, or a token revoked elsewhere, still reaches PostgREST/Storage
 * stale and gets a 401. One forced token request and one resend fix that
 * without the caller noticing. Only user-token requests are retried (an anon
 * request cannot get better), only once, and never with a streamed body
 * (it cannot be sent twice).
 */
const DATA_PATHS = ['/rest/v1/', '/storage/v1/', '/functions/v1/']

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

export function createAuthRetryFetch(opts: {
  fetchImpl: typeof fetch
  getToken: (o?: { forceRefresh?: boolean }) => Promise<string | null>
  anonKey: string
}): typeof fetch {
  const { fetchImpl, getToken, anonKey } = opts
  return async (input, init) => {
    const response = await fetchImpl(input, init)
    if (response.status !== 401) return response
    if (!DATA_PATHS.some((path) => urlOf(input as RequestInfo | URL).includes(path))) return response
    if (typeof ReadableStream !== 'undefined' && init?.body instanceof ReadableStream) return response

    const headers = new Headers(init?.headers)
    const sent = headers.get('Authorization')?.replace(/^Bearer\s+/i, '')
    if (!sent || sent === anonKey) return response

    const fresh = await getToken({ forceRefresh: true })
    if (!fresh || fresh === sent) return response

    headers.set('Authorization', `Bearer ${fresh}`)
    return fetchImpl(input, { ...init, headers })
  }
}
